import { LocalDate } from "@js-joda/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import type { BodyOf, Messages } from "../../../../../core/api";
import { auth } from "../../../../../core/auth";
import useApi from "../../../../../shared/hooks/useApi";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import { queryKeys } from "../../../../../shared/query-keys";
import { PatientProfilePatient } from "../../../patientProfileModal.types";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import { getAddressTimezone } from "../addresses/addressTimezone";

type PatientPatchBody = BodyOf<"patch", "/agencies/:agencyId/patients/:patientId">;

export type SummaryDraft = {
  address2: string;
  addressInstructions: string;
  adultServiceWorkerEmail: string;
  adultServiceWorkerName: string;
  agencyBranchIds: PatientProfilePatient["agencyBranchIds"];
  agencyTeamId: number | null;
  assignedCoordinator: number | null;
  clinicalSeverityLevel:
    | NonNullable<
        BodyOf<"patch", "/agencies/:agencyId/patients/:patientId">["clinicalSeverityLevel"]
      >
    | "";
  currentOfficeId: number | null;
  dateOfBirth: string;
  firstName: string;
  gender: "F" | "M" | "";
  lastName: string;
  mainLanguage: string;
  middleName: string;
  orderingPcpNpi: string;
  pimsId: string;
  secondaryLanguage: string;
  ssn: string;
  startOfCare: string;
  wageParity: boolean | null;
};

export type SummaryEditableField = keyof SummaryDraft | "address";

function dateToInputValue(value: LocalDate | null) {
  return value === null ? "" : value.toString();
}

export function createSummaryDraft(patient: PatientProfilePatient): SummaryDraft {
  return {
    address2: patient.address2 ?? "",
    addressInstructions: patient.addressInstructions ?? "",
    adultServiceWorkerEmail: patient.adultServiceWorkerEmail ?? "",
    adultServiceWorkerName: patient.adultServiceWorkerName ?? "",
    agencyBranchIds: patient.agencyBranchIds,
    agencyTeamId: patient.agencyTeamId,
    assignedCoordinator: patient.assignedCoordinator,
    clinicalSeverityLevel: patient.clinicalSeverityLevel ?? "",
    currentOfficeId: patient.currentOfficeId,
    dateOfBirth: dateToInputValue(patient.dateOfBirth),
    firstName: patient.firstName,
    gender: patient.gender === "F" || patient.gender === "M" ? patient.gender : "",
    lastName: patient.lastName,
    mainLanguage: patient.mainLanguage ?? "",
    middleName: patient.middleName ?? "",
    orderingPcpNpi: patient.orderingPcpNpi ?? "",
    pimsId: patient.pimsId ?? "",
    secondaryLanguage: patient.secondaryLanguage ?? "",
    ssn: patient.ssn ?? "",
    startOfCare: dateToInputValue(patient.startOfCare),
    wageParity: patient.wageParity ?? null,
  };
}

function nullableText(value: string) {
  return value.trim() === "" ? null : value;
}

type UsePatientProfileSummaryControllerParams = {
  actions: PatientProfileBridgeActions;
  canEdit: boolean;
  patient: PatientProfilePatient;
};

export function usePatientProfileSummaryController(
  params: UsePatientProfileSummaryControllerParams
) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const { actions, canEdit, patient } = params;
  const canEditSsn = auth.isAllowedTo("show_patient_ssn");
  const summaryDraft = React.useMemo(() => createSummaryDraft(patient), [patient]);
  const [activeField, setActiveField] = React.useState<SummaryEditableField | null>(null);
  const [editingBlocks, setEditingBlocks] = React.useState({
    contactInformation: false,
    personalDetails: false,
    servicesInformation: false,
    status: false,
  });
  const [fieldDraft, setFieldDraft] = React.useState<SummaryDraft | null>(null);
  const [addressAutocompleteDraft, setAddressAutocompleteDraft] = React.useState<{
    addressText: string;
    components: Messages["AddressComponents"];
  } | null>(null);
  const isContactInformationEditing = canEdit && editingBlocks.contactInformation;
  const isPersonalDetailsEditing = canEdit && editingBlocks.personalDetails;
  const isServicesInformationEditing = canEdit && editingBlocks.servicesInformation;
  const isStatusEditing = canEdit && editingBlocks.status;
  const isSummaryEditing =
    isStatusEditing ||
    isPersonalDetailsEditing ||
    isContactInformationEditing ||
    isServicesInformationEditing;
  const activeDraft = fieldDraft ?? summaryDraft;

  const invalidatePatient = React.useCallback(() => {
    const queryKey = queryKeys.patient.get(patient.id);

    queryClient.invalidateQueries({ queryKey });
    queryClient.refetchQueries({ queryKey, type: "active" });
  }, [patient.id, queryClient]);

  const updateField = <TKey extends keyof SummaryDraft>(key: TKey, value: SummaryDraft[TKey]) =>
    setFieldDraft((currentDraft) =>
      currentDraft === null ? currentDraft : { ...currentDraft, [key]: value }
    );

  const activateField = (field: SummaryEditableField) => {
    if (!canEdit) {
      return;
    }

    setActiveField(field);
    setFieldDraft(summaryDraft);
  };

  const cancelField = () => {
    setActiveField(null);
    setAddressAutocompleteDraft(null);
    setFieldDraft(null);
  };

  const toggleSummaryEditing = () => {
    const shouldEdit = !isSummaryEditing;

    cancelField();
    setEditingBlocks({
      contactInformation: shouldEdit,
      personalDetails: shouldEdit,
      servicesInformation: shouldEdit,
      status: shouldEdit,
    });
  };

  const openChangeStatus = () => {
    actions.openChangeStatus({
      patient,
      onDone: () => {
        queryClient.invalidateQueries({
          queryKey: ["patientProfileModal", patient.id, "statusChanges"],
        });
        invalidatePatient();
      },
    });
  };

  const openAddPhoneNumber = () => {
    actions.openAddPhoneNumber({
      patient,
      onDone: invalidatePatient,
    });
  };

  const openCertificationPeriod = () => {
    actions.openCertificationPeriod({
      patient,
      onDone: invalidatePatient,
    });
  };

  const deletePhoneNumberMutation = useMutation({
    mutationFn: (phoneNumber: PatientProfilePatient["phoneNumbers"][number]) =>
      api.delete("./patients/:patientId/phonenumbers/:phoneNumberId", {
        path: {
          patientId: patient.id,
          phoneNumberId: phoneNumber.id,
        },
      }),
    onSuccess: (_, phoneNumber) => {
      queryClient.setQueryData(queryKeys.patient.get(patient.id), (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object" ||
          !("phoneNumbers" in currentPatient) ||
          !Array.isArray(currentPatient.phoneNumbers)
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          phoneNumbers: currentPatient.phoneNumbers.filter(
            (item: PatientProfilePatient["phoneNumbers"][number]) => item.id !== phoneNumber.id
          ),
        };
      });
      invalidatePatient();
      toast({
        description: "Phone number deleted successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not delete phone number.",
        status: "error",
      });
    },
  });

  const handleDeletePhoneNumber = (phoneNumber: PatientProfilePatient["phoneNumbers"][number]) => {
    const shouldDelete = window.confirm("Are you sure you want to remove this phone number?");

    if (!shouldDelete) {
      return;
    }

    deletePhoneNumberMutation.mutate(phoneNumber);
  };

  const openAddressPicker = () => {
    actions.openCustomAddress({ patient });
  };

  const updatePrimaryAddressMutation = useMutation({
    mutationFn: async (mutationParams: {
      addressText: string;
      components: Messages["AddressComponents"];
    }) => {
      const timezone = await getAddressTimezone(mutationParams.components.location);

      const response = await api.patch("/agencies/:agencyId/patients/:patientId", {
        path: { patientId: patient.id },
        body: {
          id: patient.id,
          address: {
            text: mutationParams.addressText,
            timezone,
            components: mutationParams.components,
          },
          isCustomAddress: false,
        },
      });

      return { response, timezone };
    },
    onSuccess: ({ timezone }, mutationParams) => {
      queryClient.setQueryData(queryKeys.patient.get(patient.id), (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          address: {
            text: mutationParams.addressText,
            timezone,
            components: mutationParams.components,
          },
          isCustomAddress: false,
        };
      });
      invalidatePatient();
      setActiveField(null);
      setAddressAutocompleteDraft(null);
      toast({
        description: "Address updated successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update address.",
        status: "error",
      });
    },
  });

  const updateBranchesMutation = useMutation({
    mutationFn: (agencyBranchIds: PatientProfilePatient["agencyBranchIds"]) =>
      api.put("./patients/:patientId/branches", {
        body: { agencyBranchIds },
        path: { patientId: patient.id },
      }),
    onSuccess: (_, agencyBranchIds) => {
      queryClient.setQueryData(queryKeys.patient.get(patient.id), (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          agencyBranchIds,
        };
      });
      invalidatePatient();
      setActiveField(null);
      setFieldDraft(null);
      toast({
        description: "Branches updated successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update branches.",
        status: "error",
      });
    },
  });

  const runEligibilityCheckMutation = useMutation({
    mutationFn: () =>
      api.post("./patients/:patientId/check_eligibility", { path: { patientId: patient.id } }),
    onSuccess: (response) => {
      queryClient.setQueryData(queryKeys.patient.get(patient.id), (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          eligibiltyStatus: response.status,
        };
      });
      invalidatePatient();
      toast({
        description: "Eligibility check completed.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not run eligibility check.",
        status: "error",
      });
    },
  });

  const updateBlacklistMutation = useMutation({
    mutationFn: async (caregiverIds: NonNullable<PatientProfilePatient["blacklisted"]>) => {
      const checkResponse = await api.post("./patients/:patientId/check_caregivers_blacklist", {
        body: { ids: caregiverIds },
        path: { patientId: patient.id },
      });
      const shouldUnassignVisits =
        checkResponse.hasFutureVisits === true
          ? window.confirm(
              "The patient has future visits with these caregivers. Unassign caregivers from these visits?"
            )
          : false;

      await api.post("./patients/:patientId/update_caregivers_blacklist", {
        body: {
          toDelete: checkResponse.toDelete,
          toInsert: checkResponse.toInsert,
          unassignVisits: shouldUnassignVisits,
        },
        path: { patientId: patient.id },
      });

      return caregiverIds;
    },
    onSuccess: (caregiverIds) => {
      queryClient.setQueryData(queryKeys.patient.get(patient.id), (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          blacklisted: caregiverIds,
        };
      });
      invalidatePatient();
      toast({
        description: "Caregivers blacklist updated successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update caregivers blacklist.",
        status: "error",
      });
    },
  });

  const updatePatientMutation = useMutation({
    mutationFn: (nextSummaryDraft: SummaryDraft) => {
      const body: PatientPatchBody = {
        id: patient.id,
        address2: nullableText(nextSummaryDraft.address2),
        addressInstructions: nullableText(nextSummaryDraft.addressInstructions),
        adultServiceWorkerEmail: nullableText(nextSummaryDraft.adultServiceWorkerEmail),
        adultServiceWorkerName: nullableText(nextSummaryDraft.adultServiceWorkerName),
        agencyTeamId: nextSummaryDraft.agencyTeamId as PatientPatchBody["agencyTeamId"],
        assignedCoordinator:
          nextSummaryDraft.assignedCoordinator as PatientPatchBody["assignedCoordinator"],
        clinicalSeverityLevel: nextSummaryDraft.clinicalSeverityLevel || null,
        currentOfficeId: (nextSummaryDraft.currentOfficeId ??
          undefined) as PatientPatchBody["currentOfficeId"],
        dateOfBirth:
          nextSummaryDraft.dateOfBirth === ""
            ? null
            : LocalDate.parse(nextSummaryDraft.dateOfBirth),
        firstName: nextSummaryDraft.firstName.trim(),
        gender: nextSummaryDraft.gender || null,
        lastName: nextSummaryDraft.lastName.trim(),
        mainLanguage: nullableText(nextSummaryDraft.mainLanguage),
        middleName: nullableText(nextSummaryDraft.middleName),
        orderingPcpNpi: nullableText(nextSummaryDraft.orderingPcpNpi),
        pimsId: nullableText(nextSummaryDraft.pimsId),
        secondaryLanguage: nullableText(nextSummaryDraft.secondaryLanguage),
        startOfCare:
          nextSummaryDraft.startOfCare === ""
            ? null
            : LocalDate.parse(nextSummaryDraft.startOfCare),
        wageParity: nextSummaryDraft.wageParity,
      };

      if (canEditSsn) {
        body.ssn = nullableText(nextSummaryDraft.ssn);
      }

      return api.patch("/agencies/:agencyId/patients/:patientId", {
        path: { patientId: patient.id },
        body,
      });
    },
    onSuccess: (_, nextSummaryDraft) => {
      queryClient.setQueryData(["patient", "get", patient.id], (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          address2: nullableText(nextSummaryDraft.address2),
          addressInstructions: nullableText(nextSummaryDraft.addressInstructions),
          adultServiceWorkerEmail: nullableText(nextSummaryDraft.adultServiceWorkerEmail),
          adultServiceWorkerName: nullableText(nextSummaryDraft.adultServiceWorkerName),
          agencyTeamId: nextSummaryDraft.agencyTeamId as PatientPatchBody["agencyTeamId"],
          assignedCoordinator:
            nextSummaryDraft.assignedCoordinator as PatientPatchBody["assignedCoordinator"],
          clinicalSeverityLevel: nextSummaryDraft.clinicalSeverityLevel || null,
          currentOfficeId: (nextSummaryDraft.currentOfficeId ??
            undefined) as PatientPatchBody["currentOfficeId"],
          dateOfBirth:
            nextSummaryDraft.dateOfBirth === ""
              ? null
              : LocalDate.parse(nextSummaryDraft.dateOfBirth),
          firstName: nextSummaryDraft.firstName.trim(),
          gender: nextSummaryDraft.gender || null,
          lastName: nextSummaryDraft.lastName.trim(),
          mainLanguage: nullableText(nextSummaryDraft.mainLanguage),
          middleName: nullableText(nextSummaryDraft.middleName),
          orderingPcpNpi: nullableText(nextSummaryDraft.orderingPcpNpi),
          pimsId: nullableText(nextSummaryDraft.pimsId),
          secondaryLanguage: nullableText(nextSummaryDraft.secondaryLanguage),
          ...(canEditSsn ? { ssn: nullableText(nextSummaryDraft.ssn) } : {}),
          startOfCare:
            nextSummaryDraft.startOfCare === ""
              ? null
              : LocalDate.parse(nextSummaryDraft.startOfCare),
          wageParity: nextSummaryDraft.wageParity,
        };
      });
      queryClient.invalidateQueries({ queryKey: ["patient", "get", patient.id] });
      setActiveField(null);
      setFieldDraft(null);
      toast({
        description: "Summary updated successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update summary.",
        status: "error",
      });
    },
  });

  const commitField = () => {
    if (activeField === null || activeField === "address" || fieldDraft === null) {
      return;
    }

    if (activeField === "agencyBranchIds") {
      updateBranchesMutation.mutate(fieldDraft.agencyBranchIds);
      return;
    }

    updatePatientMutation.mutate(fieldDraft);
  };

  return {
    activeDraft,
    activeField,
    addressAutocompleteDraft,
    cancelField,
    commitField,
    deletePhoneNumberMutation,
    handleDeletePhoneNumber,
    isContactInformationEditing,
    isPersonalDetailsEditing,
    isServicesInformationEditing,
    isStatusEditing,
    isSummaryEditing,
    openAddPhoneNumber,
    openAddressPicker,
    openCertificationPeriod,
    openChangeStatus,
    runEligibilityCheckMutation,
    setAddressAutocompleteDraft,
    summaryDraft,
    toggleSummaryEditing,
    updateField,
    updateBlacklistMutation,
    updatePrimaryAddressMutation,
    activateField,
  };
}
